import { useEffect, useRef, useState } from 'react';
import {
  claimSpotifyPlayer,
  onSlotChange,
  placeSlot,
  slotReady,
} from '../../lib/spotifyPlayerPool';
import { useNavigate } from 'react-router-dom';
import { Check, Plus, X } from 'lucide-react';
import Modal, { ModalBody } from '../ui/Modal';
import VoterRow, { sortVotersYouFirst } from './VoterRow';
import { formatDayLabel, formatSetTimeRange } from '../../utils/lineupFormat';
import ArtistPlaceholder from './ArtistPlaceholder';

/**
 * One artist's Spotify player, with its own loading shimmer. The player itself is pooled (lib/spotifyPlayerPool): it is
 * booted once per app session and switched to this artist, then laid over this frame while the sheet is open, so a
 * second sheet shows its preview in about 0.2 s instead of booting Spotify's player again (Sam, 2026-10-20).
 */
function SpotifyPreview({ spotifyId, name, label, slotIndex = 0, big = false }) {
  const frameRef = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const el = frameRef.current;
    if (!el || !spotifyId) return undefined;
    const { slot, release } = claimSpotifyPlayer(slotIndex, spotifyId);
    slot.host.querySelector('iframe')?.setAttribute('title', `Spotify player for ${name}`);
    setReady(slotReady(slot));
    const off = onSlotChange(slot, () => setReady(slotReady(slot)));
    // Follow the frame every frame while open: the sheet slides in, scrolls and resizes, and the player rides on top.
    let raf = 0;
    const tick = () => {
      placeSlot(slot, el);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      off();
      release();
    };
  }, [spotifyId, slotIndex, name]);
  return (
    <div className="artist-voters-modal__spotify">
      {label && <p className="artist-voters-modal__member">{label}</p>}
      <div
        ref={frameRef}
        className={`artist-voters-modal__spotify-frame${big ? ' artist-voters-modal__spotify-frame--big' : ''}`}
      >
        {!ready && (
          <div className="artist-voters-modal__spotify-skeleton skeleton-shimmer">
            <span className="artist-voters-modal__spotify-loading">Loading preview…</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * An artist's sheet: photo, name, day/time/stage, Spotify, the Add button and who in your group picked it. A b2b row
 * (artist.members, from mergePerformances) shows its billing name once and each artist's Spotify stacked, with one
 * Add for the whole set (operator, 2026-09-23).
 */
export default function ArtistVotersModal({
  artist,
  voters = [],
  viewerId,
  totalMemberCount,
  showBlockedFootnote = false,
  context = 'group',
  selected = false,
  onToggleVote,
  onClose,
}) {
  const navigate = useNavigate();

  if (!artist) return null;

  const performers = artist.members ?? [artist];
  const previews = performers.filter((p) => p.spotify_artist_id);

  const shown = sortVotersYouFirst(voters, viewerId);
  const timeRange = formatSetTimeRange([artist]);
  const canToggleVote = typeof onToggleVote === 'function';
  const showVoterList = context === 'group' || context === 'mutual';

  const label =
    context === 'mutual'
      ? `${shown.length} friend${shown.length !== 1 ? 's' : ''} want to see this`
      : `${shown.length} of ${totalMemberCount ?? shown.length} in your group want to see this`;

  const metaParts = [
    artist.performance_day ? formatDayLabel(artist.performance_day) : null,
    timeRange,
    artist.stage,
  ].filter(Boolean);

  const openVoter = (voter) => {
    onClose?.();
    navigate(voter.id === viewerId ? '/profile' : `/profile/${voter.id}`);
  };

  return (
    <Modal isOpen onClose={onClose} size="sm" showClose={false} glassSurface={false}>
      <ModalBody className="artist-voters-modal relative">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="artist-voters-modal__close"
        >
          <X size={14} aria-hidden />
        </button>

        <div className="artist-voters-modal__header mb-4 flex items-start gap-3">
          {artist.spotify_image_url ? (
            <img
              src={artist.spotify_image_url}
              alt=""
              className="h-[48px] w-[48px] shrink-0 rounded-xl object-cover"
            />
          ) : (
            <ArtistPlaceholder
              name={artist.artist_name}
              className="h-[48px] w-[48px] shrink-0 rounded-xl text-[19px]"
            />
          )}
          <div className="min-w-0 flex-1 overflow-hidden">
            <h2 className="text-body font-extrabold leading-snug text-ink">{artist.artist_name}</h2>
            {metaParts.length > 0 && (
              <p className="truncate text-micro text-[#8a7fa3]">{metaParts.join(' · ')}</p>
            )}
          </div>
        </div>

        {previews.length > 0 && (
          <div className="mb-4 flex flex-col gap-3">
            {previews.map((p, i) => {
              const name = p.canonical_artist_name || p.artist_name;
              return (
                <SpotifyPreview
                  key={p.spotify_artist_id}
                  slotIndex={i}
                  spotifyId={p.spotify_artist_id}
                  name={name}
                  label={performers.length > 1 ? name : null}
                  big={previews.length === 1}
                />
              );
            })}
          </div>
        )}

        {canToggleVote && (
          <button
            type="button"
            className="vote-status-footer"
            aria-pressed={selected}
            aria-label={selected ? 'Going' : 'Add'}
            onClick={() => onToggleVote(artist.artist_id, artist.performance_day)}
          >
            <span
              className={`artist-row__vote-toggle ${selected ? 'artist-row__vote-toggle--selected' : ''}`}
              aria-hidden
            >
              {selected ? (
                <Check size={13} strokeWidth={2.4} className="text-ink" />
              ) : (
                <Plus size={13} strokeWidth={2.2} className="text-ink/70" />
              )}
            </span>
            <span className="vote-status-footer__label">{selected ? 'Going' : 'Add'}</span>
          </button>
        )}

        {showVoterList && (
          <>
            <p className="artist-voters-modal__label mb-3">{label}</p>

            {shown.length === 0 ? (
              <p className="py-4 text-sm text-ink/60">No picks yet.</p>
            ) : (
              <div className="space-y-0.5">
                {shown.map((voter) => (
                  <VoterRow
                    key={voter.id}
                    voter={voter}
                    isYou={voter.id === viewerId}
                    onPress={openVoter}
                    layout="modal"
                  />
                ))}
              </div>
            )}
          </>
        )}

        {showBlockedFootnote && showVoterList && (
          <p className="blocked-footnote mt-4">
            Blocked members are hidden from your view, not removed from the group
          </p>
        )}
      </ModalBody>
    </Modal>
  );
}
